import { useEffect, useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import type { CustomerOnboardingQueueEntry } from '@metron/shared';
import { Button } from '../components/ui/button.tsx';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card.tsx';
import { DataTable } from '../components/data-table/data-table.tsx';
import { DataTableColumnHeader } from '../components/data-table/data-table-column-header.tsx';
import { CreateCustomerOnboardingModal } from '../components/CreateCustomerOnboardingModal.tsx';
import { useAuth } from '../contexts/AuthContext.tsx';
import { useCustomerOnboarding } from '../hooks/useCustomerOnboarding.ts';
import { useTableUrlState } from '../hooks/useTableUrlState.ts';
import { getApiUrl } from '../config/api.ts';
import { ClipboardList } from 'lucide-react';
import { createLogger, toErrorMessage } from '@metron/shared';

const logger = createLogger('OnboardingQueuePage');

const formatDate = (dateString: string) => {
  try {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return 'Invalid date';
  }
};

const columns: ColumnDef<CustomerOnboardingQueueEntry>[] = [
  {
    accessorKey: 'waterscopeAccountId',
    header: ({ column }) => <DataTableColumnHeader column={column} title='WaterScope Account ID' />,
    cell: ({ row }) => <div className='font-medium'>{row.getValue('waterscopeAccountId')}</div>,
  },
  {
    accessorKey: 'metronId',
    header: ({ column }) => <DataTableColumnHeader column={column} title='Metron ID' />,
    cell: ({ row }) => <code className='text-xs'>{row.getValue('metronId')}</code>,
  },
  {
    accessorKey: 'accountType',
    header: ({ column }) => <DataTableColumnHeader column={column} title='Account Type' />,
  },
  {
    accessorKey: 'realEstateAccountType',
    header: ({ column }) => <DataTableColumnHeader column={column} title='Real Estate Type' />,
    cell: ({ row }) => {
      const value = row.getValue('realEstateAccountType') as string | undefined;
      return value || <span className='text-muted-foreground'>N/A</span>;
    },
  },
  {
    accessorKey: 'salesforceId',
    header: ({ column }) => <DataTableColumnHeader column={column} title='Salesforce ID' />,
    cell: ({ row }) => {
      const value = row.getValue('salesforceId') as string | undefined;
      return value || <span className='text-muted-foreground'>N/A</span>;
    },
  },
  {
    accessorKey: 'createdAt',
    header: ({ column }) => <DataTableColumnHeader column={column} title='Created At' />,
    cell: ({ row }) => (
      <span className='text-sm text-muted-foreground'>
        {formatDate(row.getValue('createdAt'))}
      </span>
    ),
  },
];

export function OnboardingQueuePage() {
  const [entries, setEntries] = useState<CustomerOnboardingQueueEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const tableState = useTableUrlState();
  const { getAccessToken } = useAuth();
  const { createOnboardingEntry } = useCustomerOnboarding();

  const fetchEntries = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const token = await getAccessToken();
      if (!token) {
        throw new Error('No access token available');
      }

      const response = await fetch(getApiUrl('customer-onboarding'), {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch onboarding queue');
      }

      const data: { entries: CustomerOnboardingQueueEntry[] } = await response.json();
      setEntries(data.entries);
    } catch (err) {
      const errorMessage = toErrorMessage(err, 'Failed to fetch onboarding queue');
      setError(errorMessage);
      logger.error({ err }, 'Error fetching onboarding queue');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchEntries();
  }, [getAccessToken]);

  const handleCreateEntry = async (data: Parameters<typeof createOnboardingEntry>[0]) => {
    const result = await createOnboardingEntry(data);
    // Refresh the list after successful creation
    await fetchEntries();
    return result;
  };

  if (isLoading) {
    return (
      <div className='flex items-center justify-center p-8'>
        <p className='text-muted-foreground'>Loading onboarding queue...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className='flex items-center justify-center p-8'>
        <p className='text-destructive'>Error: {error}</p>
      </div>
    );
  }

  return (
    <div className='container mx-auto py-6'>
      <div className='flex items-center justify-between mb-6'>
        <div>
          <h1 className='text-3xl font-bold tracking-tight'>Customer Onboarding Queue</h1>
          <p className='text-muted-foreground mt-2'>
            View and manage pending customer onboarding entries
          </p>
        </div>
        <Button onClick={() => setCreateModalOpen(true)}>
          <ClipboardList className='mr-2 h-4 w-4' />
          Create Entry
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Onboarding Queue</CardTitle>
          <CardDescription>
            {entries.length} entr{entries.length !== 1 ? 'ies' : 'y'} in the queue
          </CardDescription>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={columns}
            data={entries}
            sorting={tableState.sorting}
            onSortingChange={tableState.onSortingChange}
            pagination={tableState.pagination}
            onPaginationChange={tableState.onPaginationChange}
          />
        </CardContent>
      </Card>

      <CreateCustomerOnboardingModal
        open={createModalOpen}
        onOpenChange={setCreateModalOpen}
        onCreateEntry={handleCreateEntry}
      />
    </div>
  );
}
